import { Collapse, Spinner, Text, Skeleton } from "@chakra-ui/react";
import { Box, Flex } from "@chakra-ui/layout";
import { PaginateQuery, PaginateResult } from "core/resources/types/shared";
import React, { useState } from "react";
import { UseQueryResult } from "react-query";
import useCardFilter from "./useCardFilter";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import NewTable from "components/NewTable";
import PrimaryButton from "components/Button/PrimaryButton";
import { Empty } from "antd";
import colors from "core/resources/theme/colors";
import Pagination from "components/Pagination";

export interface CollapsibleListCardHookFilter extends PaginateQuery {
  unidadeId: string;
}

interface CollapsibleListCardProps<T> {
  unidadeId: string;
  value: number;
  singularTitleText: string;
  pluralTitleText?: string;
  newEntryButtonText?: string;
  getDataHook?: (
    filter: CollapsibleListCardHookFilter,
    enabled?: boolean
  ) => UseQueryResult<PaginateResult<T>>;
  useColumnsHook?: () => ({
    title: string;
    dataIndex?: string;
    render?: (...args: any) => React.ReactNode;
  } & Record<string, any>)[];
  NewEntryModal?: React.FC<{
    isVisible: boolean;
    onClose: () => any;
    refetch?: () => any;
  }>;
  newEntryButtonOnClick?: () => any;
  ContentItem?: React.FC<{ item: T }>;
  isLoading?: boolean;
  setDetalheId?: (id: string) => void;
  children?: React.ReactNode;
  showHeader?: boolean;
  button?: React.ReactNode;
  onRow?: any;
}

function CollapsibleListCard<T>({
  value,
  unidadeId,
  singularTitleText,
  pluralTitleText,
  getDataHook,
  newEntryButtonText,
  newEntryButtonOnClick,
  useColumnsHook,
  ContentItem,
  NewEntryModal,
  isLoading,
  children,
  setDetalheId,
  showHeader,
  button,
  onRow,
}: CollapsibleListCardProps<T>) {
  const [collapsed, setCollapsed] = useState(true);
  const { filtro, onChangeFiltro } = useCardFilter(unidadeId);

  const [modalVisible, setModalVisible] = useState(false);
  const {
    data,
    isLoading: isLoadingHook,
    isFetching,
    refetch,
  } = getDataHook!(filtro, !collapsed);

  const columns = useColumnsHook?.();

  const _isLoading = isLoadingHook || isFetching;

  return (
    <>
      <Flex
        bg="white"
        shadow="sm"
        flexDir="column"
        rounded={16}
        p={4}
        h="fit-content"
      >
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <Text fontWeight="bold" fontSize={20}>
            {!isLoading ? value : ""}{" "}
            {isLoading ? <Skeleton h="15px" w="26px" /> : null}
            <Text fontWeight="medium" fontSize={18} display="inline">
              {value === 1
                ? singularTitleText
                : pluralTitleText ?? singularTitleText + "s"}
            </Text>
          </Text>

          <Box cursor="pointer" onClick={() => setCollapsed((state) => !state)}>
            {collapsed ? (
              <BsChevronDown fontSize={30} />
            ) : (
              <BsChevronUp fontSize={30} />
            )}
          </Box>
        </Flex>

        <Collapse in={!collapsed}>
          <Flex
            flexDir="column"
            maxH="400px"
            overflowY="auto"
            className="custom-scroll-bar"
            mb={2}
            pr={2}
            mt={4}
          >
            {useColumnsHook ? (
              <NewTable<T>
                columns={columns}
                loading={_isLoading}
                hideShadow
                size="small"
                className="antd-table-striped"
                showHeader={showHeader}
                dataSource={data?.items ?? []}
                onRow={onRow}
                customPagination={{
                  currentPage: data?.meta.currentPage ?? 0,
                  itemsPerPage: data?.meta.itemsPerPage ?? 0,
                  totalItems: data?.meta.totalItems ?? 0,
                  onChangePage: (page) => onChangeFiltro({ page }),
                }}
              />
            ) : null}

            {ContentItem ? (
              data?.items?.length ? (
                data?.items.map((item, index) => (
                  <ContentItem item={item} key={index} />
                ))
              ) : _isLoading ? (
                <Flex justifyContent="center">
                  <Spinner size="lg" color={colors.primary} my={4} />
                </Flex>
              ) : (
                <Box my={4}>
                  <Empty description="Sem registros para mostrar" />
                </Box>
              )
            ) : null}

            {!useColumnsHook && data?.meta && data?.meta.totalItems > 10 ? (
              <Pagination
                style={{ marginLeft: "auto", marginBottom: 16 }}
                current={data?.meta?.currentPage ?? 1}
                total={data?.meta?.totalItems ?? 0}
                pageSize={data?.meta?.itemsPerPage ?? 0}
                onChange={(page) => {
                  onChangeFiltro({ page });
                }}
              />
            ) : null}
          </Flex>
        </Collapse>

        {newEntryButtonText ? (
          <PrimaryButton
            onClick={
              newEntryButtonOnClick
                ? newEntryButtonOnClick
                : () => setModalVisible(true)
            }
            w="fit-content"
            ml="auto"
            isLoading={isLoading || isLoadingHook || isFetching}
          >
            {newEntryButtonText}
          </PrimaryButton>
        ) : null}

        {button}
      </Flex>

      {NewEntryModal ? (
        <NewEntryModal
          isVisible={modalVisible}
          onClose={() => setModalVisible(false)}
          refetch={refetch}
        />
      ) : null}

      {children}
    </>
  );
}

export default CollapsibleListCard;
